<template>
  <div class="home container-fluid p-0">

    <!-- HERO (full upper page) -->
    <section class="hero">
      <div class="hero__bg"></div>
      <div class="hero__overlay"></div>

      <!-- Top-right About button -->
      <router-link
        class="about-btn"
        :to="{ name: 'About' }"
        aria-label="About Gopher Housing"
      >
        About
      </router-link>

      <div class="hero__content text-center container">
        <img
          class="hero__logo mb-3"
          :src="require('@/img/logo2.png')"
          alt="Gopher Housing"
        />
        <p class="lead text-white-75 mb-4">Reviews for Housing at UMN</p>

        <!-- Enter submits; Reset clears -->
        <b-form @submit.prevent="filteredList">
          <b-input-group class="hero__search shadow-lg rounded-pill overflow-hidden">
            <b-button
              class="btn-maroon px-4 fw-semibold rounded-0 rounded-start"
              size="lg"
              type="submit"
            >
              Search
            </b-button>

            <b-form-input
              v-model="input"
              size="lg"
              class="border-0"
              placeholder="Search by hall or apartment name…"
              @keyup.enter.prevent="filteredList"
              autocomplete="off"
            />

            <b-input-group-append>
              <!-- Only this button changed: styles + icon, same logic -->
              <b-button
                class="btn-reset-ghost"
                size="lg"
                :disabled="!input"
                @click.prevent="resetSearch"
                title="Reset search"
                aria-label="Reset search"
              >
                <i class="bi bi-arrow-counterclockwise"></i>
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form>
      </div>

      <div class="hero__curve"></div>
    </section>

    <!-- FILTER BAR -->
    <section class="filters container">
      <div class="row g-3 align-items-center justify-content-center justify-content-md-between">
        <!-- Housing Type -->
        <div class="col-12 col-md-auto text-center text-md-start">
          <div class="filter-label">
            <i class="bi bi-funnel"></i>
            <span>Housing Type</span>
          </div>
          <b-dropdown
            right
            variant="link"
            toggle-class="chip chip--select"
            class="shadow-none"
            :text="value2"
          >
            <b-dropdown-item
              v-for="option in housingOptions"
              :key="option"
              @click="value2 = option; HousingSelectionFunc();"
            >
              {{ option }}
            </b-dropdown-item>
          </b-dropdown>
        </div>

        <!-- Sort By -->
        <div class="col-12 col-md-auto text-center text-md-start">
          <div class="filter-label">
            <i class="bi bi-funnel"></i>
            <span>Sort By</span>
          </div>
          <b-dropdown
            right
            variant="link"
            toggle-class="chip chip--select"
            class="shadow-none"
            :text="value || 'Choose…'"
          >
            <b-dropdown-item
              v-for="option in options"
              :key="option"
              @click="value = option; FilterFunc();"
            >
              {{ option }}
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>
    </section>

    <!-- RESULTS -->
    <h3 class="container mt-4" v-if="currentSearch">Search Results for “{{ currentSearch }}”</h3>

    <div class="container my-4 text-center" v-if="loading">
      <b-spinner variant="warning" class="me-2"></b-spinner>
      <span class="text-muted">Loading housing…</span>
    </div>

    <!-- CARD GRID -->
    <section class="container pb-5">
      <b-row class="g-4">
        <b-col
          v-for="dorm in rendered"
          :key="dorm.webID"
          cols="12" sm="6" lg="4" xl="3"
        >
          <b-card
            no-body
            class="gh-card h-100 hover-lift"
            @click="toDorm(dorm.webID)"
          >
            <b-card-img
              :src="require('@/assets/' + dorm.webID + '/' + dorm.webID + '1.jpg')"
              alt=""
              top
              class="gh-card__img"
            />
            <b-card-body>
              <div class="d-flex align-items-start justify-content-between">
                <h3 class="h5 fw-bold mb-2 text-maroon">{{ dorm.name }}</h3>
                <span class="badge bg-gold text-dark align-top">{{ dorm.reviewCount }}</span>
              </div>

              <div class="d-flex align-items-center">
                <vue3-star-ratings
                  :increment="0.1"
                  :starSize="24"
                  :glow="1"
                  :inline="true"
                  :show-rating="false"
                  active-color="var(--gh-maroon)"
                  read-only
                  v-model:rating="dorm.averages.overallAvg"
                />
                <small class="ms-2 text-muted">{{ dorm.averages.overallAvg?.toFixed(1) }}</small>
              </div>

              <div class="gh-metrics mt-3">
                <div class="metric">
                  <span class="metric__label">Social</span>
                  <span class="metric__bar"><i :style="{width: pct(dorm.averages.socialAvg)}"></i></span>
                </div>
                <div class="metric">
                  <span class="metric__label">Location</span>
                  <span class="metric__bar"><i :style="{width: pct(dorm.averages.locationAvg)}"></i></span>
                </div>
                <div class="metric">
                  <span class="metric__label">Building</span>
                  <span class="metric__bar"><i :style="{width: pct(dorm.averages.buildingAvg)}"></i></span>
                </div>
                <div class="metric">
                  <span class="metric__label">Food</span>
                  <span class="metric__bar"><i :style="{width: pct(dorm.averages.foodAvg)}"></i></span>
                </div>
              </div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </section>
  </div>
</template>

<script>
import axios from "axios";
export default {
  name: "Home",
  data() {
    return {
      info: [],
      rendered: [],
      input: "",
      currentSearch: "",
      loading: true,
      value: "",
      value2: "All",
      options: [
        "Name",
        "Best Overall",
        "# Reviews",
        "Social Rating",
        "Location Rating",
        "Building Rating",
        "Food Rating",
        "Worst Overall",
      ],
      housingOptions: ["All", "Dorms", "Apartments"],
    };
  },
  created() {
    axios
      .get("https://server-zeta-tawny.vercel.app/api/total")
      .then((response) => {
        this.loading = false;
        this.info = response.data;
        this.rendered = response.data;
      })
      .catch((error) => {
        this.loading = false;
        console.log("This is an error: " + error);
      });
  },
  methods: {
    toDorm(name) {
      this.$router.push({ name: "dorm-view", params: { dormName: name } });
    },
    toType(type) {
      this.loading = true;
      axios
        .get("https://server-zeta-tawny.vercel.app/api/" + type)
        .then((response) => {
          this.rendered = [];
          this.loading = false;
          this.info = response.data;
          this.rendered = response.data;
          this.value = "";
        })
        .catch((error) => {
          this.loading = false;
          console.log("This is an error: " + error);
        });
    },
    filteredList() {
      const q = (this.input || "").trim().toLowerCase();
      this.currentSearch = this.input.trim();
      if (!this.info || this.info.length === 0) {
        this.rendered = this.info;
        return;
      }
      if (!q) {
        // empty search behaves like reset
        this.rendered = this.info;
        this.currentSearch = "";
        return;
      }
      this.rendered = this.info.filter((item) =>
        item.name.toLowerCase().includes(q)
      );
    },
    resetSearch() {
      this.input = "";
      this.currentSearch = "";
      this.rendered = this.info;
    },
    HousingSelectionFunc() {
      if (this.value2 === "All") this.toType("total");
      if (this.value2 === "Dorms") this.toType("dorms");
      if (this.value2 === "Apartments") this.toType("apartments");
    },
    FilterFunc() {
      const cmp = (a, b) => (a > b ? 1 : a < b ? -1 : 0);
      const by = {
        "Best Overall": (a, b) => -cmp(a.averages.overallAvg, b.averages.overallAvg),
        "Worst Overall": (a, b) => cmp(a.averages.overallAvg, b.averages.overallAvg),
        "Name": (a, b) => cmp(a.name, b.name),
        "# Reviews": (a, b) => -cmp(a.reviewCount, b.reviewCount),
        "Social Rating": (a, b) => -cmp(a.averages.socialAvg, b.averages.socialAvg),
        "Location Rating": (a, b) => -cmp(a.averages.locationAvg, b.averages.locationAvg),
        "Building Rating": (a, b) => -cmp(a.averages.buildingAvg, b.averages.buildingAvg),
        "Food Rating": (a, b) => -cmp(a.averages.foodAvg, b.averages.foodAvg),
      }[this.value] || ((a, b) => cmp(a.name, b.name));
      this.rendered = [...this.rendered].sort(by);
    },
    pct(v) {
      if (v == null) return "0%";
      const x = Math.max(0, Math.min(5, Number(v)));
      return `${(x / 5) * 100}%`;
    },
  },
};
</script>

<style>
@import "https://cdn.jsdelivr.net/npm/bootstrap-icons/font/bootstrap-icons.css";

/* THEME */
:root {
  --gh-maroon: #7a0019;
  --gh-gold: #ffcc33;
  --gh-ink: #1f1f1f;
  --gh-muted: rgba(0, 0, 0, 0.55);
  --gh-card: #f7f7f8;
  --gh-radius: 18px;
}

/* HERO */
.hero {
  position: relative;
  min-height: clamp(420px, 70vh, 720px);
  display: grid;
  place-items: center;
  overflow: hidden;
  border-radius: 0 0 24px 24px;
  margin-bottom: 1.25rem;
}
.hero__bg {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #6b0016 0%, #7a0019 35%, #b31b1b 75%, #ffcc33 100%);
}
.hero__bg::after {
  content: "";
  position: absolute;
  inset: 0;
  background-image: url("data:image/svg+xml,%3Csvg width='120' height='120' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M60 0 L120 60 L60 120 L0 60 Z' fill='rgba(255,255,255,0.05)'/%3E%3C/svg%3E");
  background-size: 120px 120px;
  opacity: 0.15;
}
.hero__overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.45), rgba(0, 0, 0, 0.15));
}
.hero__content {
  position: relative;
  z-index: 1;
  text-align: center;
  padding: 1rem;
}
.hero__logo {
  max-width: 300px;
  width: 70%;
  filter: drop-shadow(0 3px 4px rgba(0, 0, 0, 0.4));
}
.hero__search {
  max-width: 780px;
  margin-inline: auto;
  backdrop-filter: blur(6px);
  background: rgba(255, 255, 255, 0.9);
}
.btn-maroon {
  background: var(--gh-maroon) !important;
  border-color: var(--gh-maroon) !important;
  color: #fff !important;
}
.hero__curve {
  position: absolute;
  bottom: -1px;
  left: 0; right: 0;
  height: 36px;
  background: radial-gradient(120% 60px at 50% -20px, rgba(0,0,0,0.12), transparent 70%);
}

/* Top-right About button */
.about-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: .5rem;
  padding: .5rem .9rem;
  border-radius: 999px;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.95rem;

  color: #fff;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.28);
  backdrop-filter: blur(6px);
  transition: all .18s ease;
}
.about-btn:hover {
  background: rgba(255, 255, 255, 0.18);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-1px);
  color: yellow
}
.about-btn:active { transform: translateY(0); }

/* FILTER BAR & CARDS (unchanged) */
.filters {
  background: linear-gradient(180deg, #fff9f9, #fff6f6);
  border: 1px solid rgba(122, 0, 25, 0.1);
  border-radius: 16px;
  padding: 1rem 1.5rem;
  margin: 1rem auto;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
}
.filter-label {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: var(--gh-maroon);
  margin-bottom: 6px;
  gap: 6px;
  font-size: 1rem;
}
.filter-label i { font-size: 1.1rem; opacity: 0.85; }
.chip--select {
  background: #fff !important;
  border: 1.5px solid rgba(122, 0, 25, 0.25) !important;
  color: var(--gh-maroon) !important;
  font-weight: 600;
  border-radius: 999px;
  padding: 0.5rem 1rem !important;
  transition: all 0.2s ease;
}
.chip--select:hover {
  border-color: var(--gh-maroon);
  background: rgba(122, 0, 25, 0.05);
}

.text-maroon { color: var(--gh-maroon) !important; }
.bg-gold { background: var(--gh-gold) !important; color: #422c00 !important; }

.gh-card {
  background: var(--gh-card);
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: var(--gh-radius);
  overflow: hidden;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
  cursor: pointer;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.04), 0 6px 18px rgba(0, 0, 0, 0.05);
}
.gh-card:hover {
  border-color: rgba(0, 0, 0, 0.12);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06), 0 18px 36px rgba(0, 0, 0, 0.09);
}
.gh-card__img { height: 180px; object-fit: cover; }
.hover-lift:hover { transform: translateY(-2px); }

.gh-metrics .metric {
  display: grid;
  grid-template-columns: 90px 1fr;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
}
.metric__label { font-size: 0.85rem; color: var(--gh-muted); }
.metric__bar {
  display: block; position: relative; height: 8px;
  border-radius: 999px; background: rgba(0, 0, 0, 0.06); overflow: hidden;
}
.metric__bar i {
  display: block; height: 100%;
  background: linear-gradient(90deg, var(--gh-gold), #ffe080 55%, #ffd45a);
  border-radius: 999px;
}

.text-white-75 { color: rgba(255, 255, 255, 0.85); }
*:focus { outline: none; box-shadow: none !important; }

/* New: icon-only ghost reset button */
.btn-reset-ghost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 0 .9rem;
  border: 0;
  border-left: 1px solid rgba(0,0,0,.08);
  border-radius: 0;                           
  background: rgba(255,255,255,.55);
  color: maroon !important;
  backdrop-filter: blur(4px);
  transition: all .18s ease;
  background-color: #ffcc33 !important;
}
.btn-reset-ghost i { font-size: 1.1rem; line-height: 1; }
.btn-reset-ghost:hover:not(:disabled) {
  background: rgba(255,255,255,.85);
  color: var(--gh-maroon);
}
.btn-reset-ghost:disabled {
  opacity: .4;
  cursor: not-allowed;
}
</style>
